import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from 'environments-lib';
import { DatasourceFileResponseIntermediate, IEnvironment } from 'interfaces-lib';
import mammoth from 'mammoth';
import {catchError, finalize, from, map, Observable, of, shareReplay, switchMap, throwError} from 'rxjs';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { FilePreviewData, FilePreviewDataDocx, FilePreviewDataImage, FilePreviewDataPdf } from './file-preview.component';

// Unable to import from library, so copied it over
interface IMammothMessage {
  type: 'error' | 'warning' | 'info';
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class FilePreviewHelperService {
  // Cache to store ongoing or completed file preview observables to prevent redundant processing
  // Cache entry is removed after completion or error, to avoid stale data
  private previewCache = new Map<string, Observable<FilePreviewData>>();

  constructor(
    private http: HttpClient,
    private identityStorage: IdentityStorage,
    private sharedServiceProviderFactory: SharedServiceProviderServiceFactory,
    @Inject(ENVIRONMENT) private environment: Partial<IEnvironment>
  ) { }

  /**
   * Retrieves file preview data for a given file datasource path.
   * @param fileDatasourcePath - Array containing [namespace, entityType, entityId, fileId]
   * @param fileData - Optional additional data for file retrieval
   * @param namespaceOverride - Optional namespace override for files outside user's current namespace, Defaults to user's namespace.
   * @returns Observable of FilePreviewData containing the file preview information
   */
  getFilePreviewDataFor(fileDatasourcePath: string[], fileData?: any, namespaceOverride?: string): Observable<FilePreviewData> {
    const cacheKey = fileDatasourcePath.join('/');
    const namespace = namespaceOverride ?? this.identityStorage.get().namespace;

    if (!this.previewCache.has(cacheKey)) {
      const preview$ = this.sharedServiceProviderFactory
        .getServiceProviderService(namespace)
        .getFileDataSourceApiService()
        .getFileData(fileDatasourcePath, fileData)
        .pipe(
          switchMap((file) => this.processFileForPreview(file, fileDatasourcePath)),
          catchError((error) => {
            this.previewCache.delete(cacheKey); // Remove from cache on error so retry is possible

            return throwError(() => error);
          }),
            finalize(() => this.previewCache.delete(cacheKey)), // Remove from cache after processing
            shareReplay(1) // Cache the successful result
        );

      this.previewCache.set(cacheKey, preview$);
    }

    const cachedPreview = this.previewCache.get(cacheKey);
    if (!cachedPreview) {
      return throwError(() => new Error('Failed to cache preview observable.'));
    }
    return cachedPreview;
  }

  /**
   * Processes a file and converts it to the appropriate preview format.
   */
  private processFileForPreview(file: DatasourceFileResponseIntermediate, fileDatasourcePath: string[]): Observable<FilePreviewData> {
    if (!file.base64EncodedContent && !file.fileBinaryData) {
      return throwError(() => new Error('File content is missing for preview.'));
    }
    switch (file.contentType) {
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return this.processDocxFile(file, fileDatasourcePath);

      case 'application/pdf':
        return this.processPdfFile(file);

      case 'application/msword':
        return this.processDocFile(file, fileDatasourcePath);

      case 'image/png':
      case 'image/jpeg':
      case 'image/jpg':
      case 'image/gif':
        return this.processImageFile(file);

      default:
        return throwError(() => new Error(`Unsupported file type: ${file.contentType}`));
    }
  }

  /**
   * Processes DOCX files by converting to HTML or falling back to backend preview.
   */
  private processDocxFile(file: DatasourceFileResponseIntermediate, fileDatasourcePath: string[]): Observable<FilePreviewData> {
    const originalFileBlob = file.fileBinaryData || (file.base64EncodedContent ?
        this.base64ToBlob(file.base64EncodedContent, file.contentType) : null);

    // Unreachable: processFileForPreview already guarantees fileBinaryData or
    // base64EncodedContent is set before dispatching here. Kept as defence-in-depth.
    /* istanbul ignore next */
    if (!originalFileBlob) {
      return throwError(() => new Error('No file content available for DOCX processing.'));
    }

    // Convert to ArrayBuffer for mammoth (the base64 branch is always valid here, per the
    // same guarantee noted above).
    const conversionPromise = file.fileBinaryData
      ? file.fileBinaryData.arrayBuffer()
      : Promise.resolve(this.base64ToArrayBuffer(file.base64EncodedContent as string));

    return from(conversionPromise).pipe(
      switchMap((arrayBuffer) => from(this.convertDocxToHtml(arrayBuffer))),
      switchMap((htmlContent) => {
        if (!htmlContent?.html) {
          console.warn('Failed to convert DOCX to HTML for preview. Falling back to /get-preview.');
          return this.getPreviewFromBackend(file.name, fileDatasourcePath, originalFileBlob);
        }

        const previewData: FilePreviewDataDocx = {
          filename: file.name,
          htmlContent: htmlContent.html,
          fileType: 'docx',
          originalFileContent: originalFileBlob,
        };

        return of(previewData);
      })
    );
  }

  /**
   * Processes PDF files by converting base64 content to Blob.
   */
  private processPdfFile(file: DatasourceFileResponseIntermediate): Observable<FilePreviewDataPdf> {
    const pdfBlob = file.fileBinaryData || (file.base64EncodedContent ?
        this.base64ToBlob(file.base64EncodedContent, 'application/pdf') : null);

    // Unreachable: processFileForPreview already guarantees fileBinaryData or
    // base64EncodedContent is set before dispatching here. Kept as defence-in-depth.
    /* istanbul ignore next */
    if (!pdfBlob) {
      return throwError(() => new Error('No file content available for PDF processing.'));
    }

    const previewData: FilePreviewDataPdf = {
      filename: file.name,
      pdfContent: pdfBlob,
      fileType: 'pdf',
      originalFileContent: pdfBlob,
    };

    return of(previewData);
  }

  /**
   * Processes DOC files by getting a presigned URL from the backend.
   */
  private processDocFile(file: DatasourceFileResponseIntermediate, fileDatasourcePath: string[]): Observable<FilePreviewDataPdf> {
    const originalFileBlob = file.fileBinaryData || (file.base64EncodedContent ?
        this.base64ToBlob(file.base64EncodedContent, file.contentType) : undefined);
    return this.getPreviewFromBackend(file.name, fileDatasourcePath, originalFileBlob);
  }

  /**
   * Processes image files (PNG, JPG, JPEG, GIF) by converting to Blob.
   */
  private processImageFile(file: DatasourceFileResponseIntermediate): Observable<FilePreviewDataImage> {
    const imageBlob = file.fileBinaryData || (file.base64EncodedContent ?
        this.base64ToBlob(file.base64EncodedContent, file.contentType) : null);

    // Unreachable: processFileForPreview already guarantees fileBinaryData or
    // base64EncodedContent is set before dispatching here. Kept as defence-in-depth.
    /* istanbul ignore next */
    if (!imageBlob) {
      return throwError(() => new Error('No file content available for image processing.'));
    }

    const previewData: FilePreviewDataImage = {
      filename: file.name,
      imageContent: imageBlob,
      fileType: 'image',
      mimeType: file.contentType,
      originalFileContent: imageBlob,
    };

    return of(previewData);
  }

  /**
   * Gets a presigned URL to a converted PDF from the backend.
   */
  private getPreviewFromBackend(
    filename: string,
    fileDatasourcePath: string[],
    originalFileContent?: Blob
  ): Observable<FilePreviewDataPdf> {
    return this.http.get<{ url: string }>(`${this.environment.kpHost}/file/get-preview/${fileDatasourcePath.join('/')}`).pipe(
      map((response) => ({
        filename,
        pdfUrl: response.url,
        fileType: 'pdf' as const,
        originalFileContent,
      })),
    );
  }

  private async convertDocxToHtml(arrayBuffer: ArrayBuffer): Promise<{ html: string; messages: IMammothMessage[] } | null> {
    try {
      const result = await mammoth.convertToHtml({ arrayBuffer });

      const errors = result.messages.filter(m => m.type === 'error');

      if (errors.length > 0) {
        console.warn('DOCX conversion had errors:', errors);
      }

      return { html: result.value, messages: result.messages };
    } catch (error) {
      console.warn('DOCX conversion failed:', error);
      return null; // We don't need to throw here; the fallback method will handle it
    }
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private base64ToBlob(base64: string, contentType: string): Blob {
    const arrayBuffer = this.base64ToArrayBuffer(base64);
    return new Blob([arrayBuffer], { type: contentType });
  }
}
